import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { TrainingReferenceLink } from '../components/BorderCard';

const StoreUpkeepPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-[#1A1A1A] mb-8">
            Store Upkeep
          </h1>
          
          <TrainingReferenceLink 
            href="https://canva.link/5xjp4ck2o2bcm8q"
            label="Store Upkeep Reference Link"
          />
        </motion.div>

        {/* Overview */}
        <FadeIn delay={0.3}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-10 md:p-12 mb-8">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D32F2F] mb-6 text-center">
              Overview
            </h3>
            <p className="text-base text-[#1A1A1A]/80 leading-relaxed max-w-3xl mx-auto text-center">
              A well-maintained store reflects our brand's commitment to quality and attention 
              to detail. This guide covers daily upkeep routines, VM standards, cleanliness 
              protocols, and best practices to ensure a premium shopping experience that aligns 
              with The Bear House aesthetic.
            </p>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-8 text-center">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Daily Checklist */}
        <FadeIn delay={0.4}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10 mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              Daily Upkeep Checklist
            </h3>
            <ul className="space-y-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Opening Routine:</strong> Dust all fixtures, check lighting, ensure all displays are in order before store opens.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Merchandise Check:</strong> Ensure all products are properly folded, hung, and sized correctly.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Floor Maintenance:</strong> Keep floors clean, spot-free, and safe for customers throughout the day.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Closing Routine:</strong> Reset displays, restock, and prepare the store for the next day.</span>
              </li>
            </ul>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* VM Standards */}
        <FadeIn delay={0.5}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10 mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              VM Standards
            </h3>
            <ul className="space-y-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Display Consistency:</strong> Follow brand guidelines for mannequin styling and product arrangement.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Color Coordination:</strong> Maintain visual harmony in color blocking and product groupings.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Signage & POS:</strong> Ensure all promotional materials are current, clean, and properly placed.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Lighting Check:</strong> Verify all spotlights and ambient lighting are functioning correctly.</span>
              </li>
            </ul>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Hygiene & Cleanliness */}
        <FadeIn delay={0.6}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10 mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              Hygiene & Cleanliness
            </h3>
            <ul className="space-y-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Trial Rooms:</strong> Clean after every use, maintain fresh environment with proper ventilation.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Counter & Cash Point:</strong> Keep the billing area organized, clean, and clutter-free.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Glass & Mirrors:</strong> Regular cleaning to maintain streak-free, spotless surfaces.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Stock Room:</strong> Organized, dust-free, and inventory properly arranged for easy access.</span>
              </li>
            </ul>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Key Reminder */}
        <FadeIn delay={0.7}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-10 text-center">
            <p className="text-xl md:text-2xl font-light text-[#1A1A1A] italic leading-relaxed">
              "A clean store is a successful store."
            </p>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default StoreUpkeepPage;
